import { useAuth } from "@/context/AuthProvider";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { Sun, Moon, LogIn, LogOut, User, Mail, ClipboardList } from "lucide-react";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import logo from "@/assets/gekkins_v4.png";

function useTheme() {
  const [dark, setDark] = useState<boolean>(() => {
    const saved = localStorage.getItem("theme");
    if (saved) return saved === "dark";
    return window.matchMedia("(prefers-color-scheme: light)").matches;
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  return { dark, toggle: () => setDark((d) => !d) };
}

export default function Navbar() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { dark, toggle } = useTheme();
  const [show, setShow] = useState(true);

  useEffect(() => {
    let lastScroll = window.scrollY;
    const handleScroll = () => {
      const currentScroll = window.scrollY;
      if (Math.abs(currentScroll - lastScroll) < 10) return;
      setShow(currentScroll <= lastScroll || currentScroll <= 50);
      lastScroll = currentScroll;
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleCopyEmail = async () => {
    await navigator.clipboard.writeText(["guillealfra", "gmail.com"].join("@"));
    toast.success("Email copied to clipboard");
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    auth?.setToken(null);
    navigate("/");
  };

  return (
    <motion.nav
      initial={{ y: 0, opacity: 1 }}
      animate={{ y: show ? 0 : -100, opacity: show ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="select-none fixed top-0 left-0 w-full px-4 pt-4 pb-2 z-99 scale-95"
    >
      <div className="relative w-full flex justify-center items-start">

        {/* ── MODO OSCURO (izquierda) ── */}
        <div className="scale-80 absolute left-0 flex gap-x-1 px-2 py-2">
          <button onClick={toggle} aria-label="Cambiar tema" className="btn_neutro px-3!">
            <motion.span
              key={dark ? "sun" : "moon"}
              initial={{ rotate: -30, opacity: 0, scale: 0.7 }}
              animate={{ rotate: 0, opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              className="flex items-center justify-center"
            >
              {dark ? <Sun size={25} /> : <Moon size={25} />}
            </motion.span>
          </button>
        </div>

        {/* ── SOCIALS (derecha) ── */}
        <div className="scale-80 absolute right-0 flex gap-x-1 px-2 py-2">
          <a href="https://github.com/guillealfra" target="_blank" rel="noopener noreferrer" className="btn_neutro p-2!">
            <FaGithub strokeWidth={3} size={30} />
          </a>
          <a
            href="https://www.linkedin.com/in/guillermo-álvarez-franganillo-5a9520369"
            target="_blank" rel="noopener noreferrer"
            className="btn_neutro p-2!"
          >
            <FaLinkedin strokeWidth={3} size={30} />
          </a>
          <button onClick={handleCopyEmail} className="btn_neutro p-2!">
            <Mail strokeWidth={2.7} absoluteStrokeWidth size={30} />
          </button>
        </div>

        {/* ── NAV PRINCIPAL (centro) ── */}
        <div className="isla w-auto max-w-3xl flex items-center justify-between px-3 py-2!">

          <Link to="/" className="flex items-center font_1 font-bold btn_sombra mr-20 group">
            <img src={logo} alt="Inicio" className="h-10 sm:h-11.5 mr-1 mb-1 transition-transform duration-200 group-hover:scale-110 group-hover:-rotate-3" />
            <h3 className="mt-1 transition-transform duration-200 group-hover:translate-x-1">Gekkins</h3>
          </Link>

          <div className="flex gap-2 items-center">
            {auth?.token ? (
              <>
                <Link to="/dashboard" className="btn_neutro text-sm">
                  Profile
                  <User strokeWidth={2.2} absoluteStrokeWidth size={16} className="ml-1.5" />
                </Link>
                <button onClick={handleLogout} className="btn_invertido text-sm">
                  <LogOut strokeWidth={2.2} absoluteStrokeWidth size={18} />
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn_neutro text-sm">
                  Login
                  <LogIn strokeWidth={2.2} absoluteStrokeWidth size={16} className="ml-1.5" />
                </Link>
                <Link to="/register" className="btn_si">
                  Waitlist
                  <ClipboardList strokeWidth={2.2} absoluteStrokeWidth size={16} className="ml-1.5" />
                </Link>
              </>
            )}
          </div>
        </div>



      </div>
    </motion.nav>
  );
}
